import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { IFutureOrder } from '../../sp-types/FutureOrderType';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import { DEFAULT_COLOR, GENERAL } from '../../sp-types/InventoryType';
import { Account, IFormulaToAccount, IRiskPercent, IRiskTolerance } from '../../sp-types/accountTypes';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { IFinancialPosition, IPositionMaterial } from '../../sp-types/financialPositionType';
import { Material } from '../../sp-types/materialType';
import { IMaterialExposure, INetExposure } from '../../sp-types/netExposureType';
import { monthsFullWord, MonthYear } from '../../utils/MonthYear/monthYear';
import { ExposureTypes, getExposureStatus } from '../../utils/RiskUtils';
import { getExposureCalc, getGeneralExposure, getNetExposure, isSameMonth } from '../../utils/exposureUtils';
import { fixMonthYear } from '../../utils/inventoryUtils';
import { addColors, getDate, getDateStr, monthDiff } from '../FinancialPositions/components/FinancialPositionsUtils';
import { aggregateByMaterial, positionsByMaterial } from '../FinancialPositions/components/PositionsDataUtils';
import { IExposureCardProps } from './ExposureCard';
import { singleMaterialCardProps } from './MaterialTrendButton';
import { months } from '../../utils/MonthYear/monthYear';
import { getMonthYearAfter } from '../FutureOrder/FutureOrders/Utils';
import { MonthYearValue } from '../../utils/MonthYear/MonthYearValue';
/* eslint-disable */

export const MULTIPLE_MATS = 'Multiple materials';
export const NO_DATA = 'No data';

export const terminalChips = [
  { key: 'Months', value: 0 },
  { key: 'Terminals', value: 1 },
];

export const valChips = [
  { key: 'Value', value: 0 },
  { key: 'Quantity', value: 1 },
];

export const priceChips = [
  { key: 'Market Price', value: 0 },
  { key: 'Actual price', value: 1 },
];

export const cumulaticeChips = [
  { key: 'Cumulative', value: 1 },
  { key: 'Incremental', value: 0 },
];

export type MatReportSummary = {
  lastPrice: number;
  quantity: number;
  monthVal: Record<string, { price: number; ratio?: number; quantity?: number }>;
  month: number;
  unit?: string;
  ratio: number;
};
export type DashboardSummaryMap = Record<string, MatReportSummary>;

export type DashboardMap = Record<
  string,
  {
    sumValue: number;
    valuesArray: { date: MonthYear; value: number }[];
  }
>;

export const reportsToSummaryMap = (
  inventoryReports: IInventoryReportType[],
  selectedYear: number,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  isActualPrice: boolean,
): DashboardSummaryMap => {
  const now = new Date();
  return inventoryReports
    .map((material) => fixMonthYear(material))
    .filter(({ ratio }) => !ratio || ratio === 0) //remove ratio entries
    .sort((a: IInventoryReportType, b: IInventoryReportType) => a.reportingTime.seconds - b.reportingTime.seconds)
    .reduce((acc, { formulaId, quantity, monthYear, ratio, actualPrice }) => {
      acc[formulaId] = acc[formulaId] || { quantity: 0, lastPrice: 0, monthVal: {}, month: 0, ratio: 0 };

      const price = isActualPrice && actualPrice ? actualPrice : pricesMap[formulaId];

      const materialPrice = +(quantity * price);

      if (
        monthYear.year > now.getFullYear() ||
        (monthYear.year === now.getFullYear() && monthYear.month >= now.getMonth()) //we take from prev month -> future months
      ) {
        acc[formulaId] = {
          lastPrice: acc[formulaId].lastPrice + materialPrice,
          month: monthYear.month,
          quantity: acc[formulaId].quantity + quantity,
          monthVal: acc[formulaId].monthVal,
          unit: materialsMap[formulaId]?.unit,
          ratio: acc[formulaId].ratio + (ratio || 0),
        };
      }
      const monthKey = `${monthYear.year}-${monthYear.month}`;
      acc[formulaId].monthVal[monthKey] = {
        price: (acc[formulaId].monthVal[monthKey]?.price || 0) + materialPrice,
        ratio,
        quantity: (acc[formulaId].monthVal[monthKey]?.quantity || 0) + quantity,
      };
      return acc;
    }, {} as DashboardSummaryMap);
};

export const getEntriesFiltered = (
  allMaterials: DashboardSummaryMap,
  currMaterialId?: string,
): [string, MatReportSummary][] => {
  if (currMaterialId) {
    const remainSum = Object.entries(allMaterials)
      .filter(([materialId, _]) => materialId !== currMaterialId)
      .reduce((acc, [_, { lastPrice: sumPrice }]) => acc + sumPrice, 0);
    allMaterials['placeholder'] = { lastPrice: remainSum, quantity: 0, monthVal: {}, month: 0, ratio: 0 };
  }

  return Object.entries(allMaterials).filter(
    ([formulaId]) => !currMaterialId || [currMaterialId, 'placeholder'].includes(formulaId),
  );
};

export const invReportsToStackedBar = (
  materialsEntries: [string, MatReportSummary][],
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  isQuantity: boolean = false,
): { formulaId: string; fieldName: string; color: string; valueArray: MonthYearValue[] }[] =>
  materialsEntries.map(([formulaId, { monthVal }]) => ({
    formulaId,
    fieldName: materialsMap[formulaId]?.name,
    color: colorsMap[formulaId] || DEFAULT_COLOR,
    valueArray: ['_storag', ...months]
      .map((_, monthIndex) => getMonthYearAfter(new Date(), monthIndex - 1))
      .map(({ month, year }) => ({
        value: (isQuantity ? monthVal[`${year}-${month}`]?.quantity : monthVal[`${year}-${month}`]?.price) || 0,
        date: { month, year },
      })),
  }));

export const mapPositions = (
  positions: IFinancialPosition[],
  formulaId: string,
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  pricesMap: Record<string, number>,
  isAccumulate: boolean,
): StackedBarChartElement[] => {
  const aggPositions = aggregateByMaterial(
    positionsByMaterial(addColors(positions), false),
    materialsMap,
    colorsMap,
    pricesMap,
  );

  const dashboardMap = calulateMonthSpread(aggPositions, isAccumulate);

  return materialMapToFiltered(
    dashboardMap,
    colorsMap,
    materialsMap,
    pricesMap,
    formulaId === GENERAL_ID ? undefined : formulaId,
  );
};

type dateValue = { date: string; value: number };
const makeMap = (values: dateValue[]): Record<string, number> =>
  values.reduce((acc, { date, value }) => ({ ...acc, [date]: value }), {});

const joinOverlapDates = (dateVals?: dateValue[]): dateValue[] =>
  (dateVals &&
    Object.entries(
      dateVals.reduce(
        (acc, { date, value }) => ({ ...acc, [date]: (acc[date] || 0) + value }),
        {} as Record<string, number>,
      ),
    ).map(([date, value]) => ({ date, value }))) ||
  [];

const joinMonthArrays = (existingVals: dateValue[], addedValues: dateValue[]): dateValue[] => {
  const existingMap = makeMap(existingVals);
  const addingMap = makeMap(addedValues);

  return [
    ...existingVals.map(({ date, value }) => ({ date, value: value + (addingMap[date] || 0) })),
    ...(addedValues.filter(({ date }) => !existingMap[date]) || []),
  ];
};

const makeExposureCalendar = (fromDate: Date, toDate: Date, value: number, filterFrom: Date): dateValue[] =>
  Array.from({ length: monthDiff(fromDate, toDate) })
    .map((_, i) => getDate(fromDate, i))
    .filter((date) => date > filterFrom || isSameMonth(date, filterFrom))
    .map((date) => ({ date: getDateStr(date), value }));

const getMonthYear = (date: string): MonthYear => ({ year: +date.substring(0, 4), month: +date.substring(4) });

export const calulateMonthSpread = (materials: IPositionMaterial[], isAccumulate: boolean): DashboardMap =>
  materials.reduce((result, { totalPrice, formulaId, positions }) => {
    const jointArray = isAccumulate
      ? positions
          ?.map(({ totalPrice, expiryDate, createDate }) =>
            makeExposureCalendar(createDate, expiryDate, totalPrice, new Date()),
          )
          .reduce((res, calendar) => joinMonthArrays(res, calendar), []) || []
      : joinOverlapDates(
          positions?.map(({ totalPrice, expiryDate }) => ({
            date: getDateStr(expiryDate),
            value: totalPrice,
          })),
        );

    const valuesArray = fillAllMonths(jointArray).map(({ date, value }) => ({ value, date: getMonthYear(date) })) || [];

    return { ...result, [formulaId]: { sumValue: totalPrice, valuesArray } };
  }, {} as DashboardMap);

const getCurrMonthValue = (valuesArray: { date: MonthYear; value: number }[], now: Date): number =>
  valuesArray.find(({ date }) => date.year === now.getFullYear() && date.month === now.getMonth() + 1)?.value || 0;

export const materialMapToFiltered = (
  allMaterials: DashboardMap,
  colorsMap: Record<string, string>,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  selectedMaterial?: string,
): StackedBarChartElement[] => {
  if (selectedMaterial) {
    const otherMaterials = Object.entries(allMaterials).filter(([materialId, _]) => materialId !== selectedMaterial);
    const remainSum = otherMaterials?.reduce((acc, [_, { sumValue }]) => acc + sumValue, 0) || 0;
    const now = new Date();

    const currMonthVal =
      otherMaterials?.reduce((acc, [_, { valuesArray }]) => acc + getCurrMonthValue(valuesArray, now), 0) || 0;

    allMaterials['placeholder'] = {
      sumValue: remainSum,
      valuesArray: [{ date: { year: now.getFullYear(), month: now.getMonth() + 1 }, value: currMonthVal }],
    };
  }

  return Object.entries(allMaterials)
    .filter(([formulaId, _]) => !selectedMaterial || [selectedMaterial, 'placeholder'].includes(formulaId))
    .map(
      ([formulaId, { sumValue, valuesArray }]): StackedBarChartElement => ({
        formulaId,
        fieldName: materialsMap[formulaId]?.name,
        unit: materialsMap[formulaId]?.unit,
        price: pricesMap[formulaId] || 0,
        color: colorsMap[formulaId] || DEFAULT_COLOR,
        valueArray: valuesArray,
        sumValue,
      }),
    );
};

const fillAllMonths = (dateValues: dateValue[]): dateValue[] => {
  const now = new Date();
  const valueMap = makeMap(dateValues);

  const dates = monthsFullWord
    .map((_, i) => getDateStr(getDate(now, i)))
    .map((date: string) => ({ date, value: valueMap[date] || 0 }));

  return dates;
};

export const getMaterialsFromRows = (
  inventoryReports: IInventoryReportType[],
  futureOrders: IFutureOrder[],
): string[] =>
  Array.from(
    new Set([
      ...inventoryReports.map((a) => a.formulaId),
      ...futureOrders.reduce(
        (acc, { supplierExposure }) => [...acc, ...(supplierExposure?.map((b) => b.formulaId) || [])],
        [] as string[],
      ),
    ]),
  );

export const getMaterialStatusCards = (
  materialIds: string[],
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  exposureType: string,
) => {
  const materialCards = materialIds.map((formulaId, i) => ({
    formulaId,
    materialName: materialsMap[formulaId]?.name || GENERAL,
    exposureStatus: exposureType,
    cardColor: colorsMap[formulaId] || '',
  }));
  return materialCards;
};

export const yearRange = (startingYear: number = 2000, numberOfYears: number = 8) =>
  Array.from(Array(numberOfYears)).map((_, i) => startingYear + i);

const convertToUSD = (matExposure: IMaterialExposure, convertRate: number): IMaterialExposure => ({
  ...(matExposure || {}),
  financialPositions: matExposure?.financialPositions * convertRate || 0,
  futureOrder: matExposure?.futureOrder * convertRate || 0,
  inventorySum: matExposure?.inventorySum * convertRate || 0,
});

const calcGeneralVolatility = (
  exposure: INetExposure,
  materialsMap: Record<string, Material>,
  convertionMap: Record<string, number>,
): number => {
  const matExposures = Object.entries(exposure.materialsExposure)
    .filter(([formulaId]) => formulaId !== GENERAL_ID)
    .filter(([formulaId]) => typeof materialsMap[formulaId]?.volatility === 'number')
    .map(([formulaId, matExposure]) => ({
      exposure: Math.abs(getExposureCalc(matExposure)) * (convertionMap[formulaId] || 1),
      volatility: materialsMap[formulaId].volatility!,
    }));

  const totalExposure = matExposures.reduce((acc, m) => acc + m.exposure, 0);
  if (totalExposure === 0) return 0;
  return matExposures.reduce((acc, m) => acc + m.exposure * m.volatility, 0) / totalExposure;
};

const calcGeneralMean = (
  exposure: INetExposure,
  materialsMap: Record<string, Material>,
  convertionMap: Record<string, number>,
): number => {
  const matExposures = Object.entries(exposure.materialsExposure)
    .filter(([formulaId]) => formulaId !== GENERAL_ID)
    .filter(([formulaId]) => typeof materialsMap[formulaId]?.volatilityMean === 'number')
    .map(([formulaId, matExposure]) => ({
      exposure: Math.abs(getExposureCalc(matExposure)) * (convertionMap[formulaId] || 1),
      mean: materialsMap[formulaId].volatilityMean!,
    }));

  const totalExposure = matExposures.reduce((acc, m) => acc + m.exposure, 0);
  if (totalExposure === 0) return 0;
  return matExposures.reduce((acc, m) => acc + m.exposure * m.mean, 0) / totalExposure;
};

export const getExposureCardData = (
  exposureData: INetExposure,
  accountMaterialData: IFormulaToAccount,
  accountRiskPerc: IRiskPercent,
  convertionMap: Record<string, number>,
  materialsMap: Record<string, Material>,
  currentMaterial?: Material | null,
): IExposureCardProps => {
  const currentExposure =
    currentMaterial && currentMaterial.id !== GENERAL
      ? convertToUSD(exposureData.materialsExposure[currentMaterial.id], convertionMap[currentMaterial.id])
      : getGeneralExposure(exposureData, convertionMap);
  const netExposure = currentExposure.inventorySum + currentExposure.financialPositions - currentExposure.futureOrder;
  const riskPercent: IRiskPercent =
    currentMaterial && currentMaterial.id !== GENERAL
      ? {
          volatility: currentMaterial.volatility ?? 0,
          volatilityMean: currentMaterial.volatilityMean,
          riskBackTesting: currentMaterial.riskBackTesting ?? [],
        }
      : {
          ...accountRiskPerc,
          volatility: calcGeneralVolatility(exposureData, materialsMap, convertionMap),
          volatilityMean: calcGeneralMean(exposureData, materialsMap, convertionMap),
        };
  return {
    gaugeBarProps: { riskTolerance: accountMaterialData.RiskTolerance, currentValue: netExposure },
    netExposure,
    riskPercent,
    selectedMaterial: currentMaterial?.id || '',
    totalAmounts: currentExposure,
  };
};

export const getFormulasCardData = (
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  convertionMap: Record<string, number>,
  formulasToAccount: IFormulaToAccount[],
  exposureData: INetExposure,
  addMaterials?: string[],
  includeGeneral: boolean = true,
  netExposureValues?: Record<string, number>,
): singleMaterialCardProps[] => {
  // Helper: get net exposure value — prefer new pre-computed values if available
  const getNetExp = (formulaId: string, isTotal: boolean) =>
    netExposureValues
      ? (netExposureValues[formulaId] ?? 0)
      : getNetExposure(exposureData, convertionMap, formulaId, isTotal);

  const generalCard = includeGeneral ? [{
    formulaId: GENERAL_ID,
    materialName: 'General',
    exposureStatus: getExposureStatus(
      DEFAULT_RISK_TOLERANCE,
      getNetExp(GENERAL_ID, true),
    ),
    cardColor: '#6b7280',
  }] : [];

  return [
    ...generalCard,
    ...formulasToAccount.map(({ formulaId, displayName, quoteSourceSymbol, isPriceWatchOnly }) => ({
      formulaId,
      quoteSourceSymbol,
      materialName:
        displayName ||
        materialsMap[formulaId]?.name ||
        materialsMap[formulaId.replace(/[cC]1$/, '')]?.name ||
        formulaId.replace(/[cC]1$/, ''),
      exposureStatus: getExposureStatus(
        formulasToAccount.find((item) => item.formulaId === formulaId)?.RiskTolerance || DEFAULT_RISK_TOLERANCE,
        getNetExp(formulaId, formulaId === GENERAL_ID),
      ),
      cardColor: colorsMap[formulaId] || '',
      isPriceWatchOnly,
    })),
    ...(addMaterials || []).map((formulaId) => ({
      formulaId,
      materialName:
        materialsMap[formulaId]?.name ||
        materialsMap[formulaId.replace(/[cC]1$/, '')]?.name ||
        formulaId.replace(/[cC]1$/, ''),
      exposureStatus: ExposureTypes.stable,
      cardColor: colorsMap[formulaId] || '',
    })),
  ];
};

// Hardcoded last-resort fallback (±1M safe, ±5M danger)
export const DEFAULT_RISK_TOLERANCE: IRiskTolerance = {
  dangerMinus: -5000000, warnMinus: -1000000, warnPlus: 1000000, dangerPlus: 5000000,
};

/**
 * Resolve risk tolerance with fallback chain:
 *   per-formula (from store) → account.riskTolerance → DEFAULT_RISK_TOLERANCE
 * All-zero values are treated as "not configured".
 */
const isRTConfigured = (rt?: IRiskTolerance | null): rt is IRiskTolerance =>
  !!rt && (rt.dangerMinus !== 0 || rt.warnMinus !== 0 || rt.warnPlus !== 0 || rt.dangerPlus !== 0);

const resolveRT = (account: Account, rt?: IRiskTolerance | null): IRiskTolerance =>
  isRTConfigured(rt) ? rt : isRTConfigured(account.riskTolerance) ? account.riskTolerance : DEFAULT_RISK_TOLERANCE;

export const getFormulaToAccountById = (account: Account, formulaId: string): IFormulaToAccount => {
  // Check account.formulasToAccount first — works for both per-material entries
  // AND for 'general' entries that were edited locally (upserted into Zustand store)
  const fromStore = account.formulasToAccount.find((item) => item.formulaId === formulaId);
  if (fromStore) {
    return { ...fromStore, RiskTolerance: resolveRT(account, fromStore.RiskTolerance) };
  }

  // For general view or unknown formula, return with fallback thresholds
  return {
    formulaId: formulaId === 'general' || formulaId === GENERAL_ID ? 'general' : '',
    RiskTolerance: resolveRT(account),
    RoutePriority: [],
    bufferStrategy: 0,
  };
};
